import store from 'redux/store';
import { deleteConfig } from 'redux/slices/configSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditConfigItem } from './edit/showEditConfigItem';

type ConfigItemEntity = Extract<ActionEntity, { type: 'config_item' }>;

const isAllConfigItem = (entities: readonly ActionEntity[]): entities is readonly ConfigItemEntity[] =>
  entities.every((entity) => entity.type === 'config_item');

/**
 * Action definitions for configuration items. `createConfigItem` /
 * `addToTransportConfigItem` are scoped; `edit` (single-only) and `delete` are
 * contextual. `deleteConfig` is keyed by the config key; delete now confirms via
 * the shared executor (was an immediate, unconfirmed delete).
 */
export const configItemActionDefinitions: ActionDefinitions = [
  {
    id: 'createConfigItem',
    scope: 'type',
    label: 'New Config',
    resolve: () => () => showEditConfigItem({ mode: 'create' }),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllConfigItem(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => showEditConfigItem({ mode: 'edit', id: only.id });
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllConfigItem(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      const single = entities.length === 1 ? entities[0] : null;
      return deleteExecutor(entities, (entity) => store.dispatch(deleteConfig(entity.key)), {
        refresh,
        confirmBody: single
          ? `Are you sure you want to delete the config item "${single.key}"?`
          : `Are you sure you want to delete these ${entities.length} config items?`,
      });
    },
  },
  {
    id: 'addToTransportConfigItem',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllConfigItem(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'config_item',
        entities.map((entity) => entity.id),
        { objectLabel: single ? single.key : 'Selected configs' }
      );
    },
  },
];
